import { Controller } from "@hotwired/stimulus"
import { QUESTIONS_PARAM, paramFromUrl, parseSpec, specMatchesNumber } from "./reading_focus"
import { locateQuote, scrollBehavior, sectionHeading, sectionLabel } from "./text_anchor"

/**
 * Chapter Quiz (theme-appraisal "Immersive Editorial")
 *
 * Rebuilds the authored end-of-chapter "Review Exercises" +
 * "Suggested Solutions to Review Exercises" ordered lists as an interactive
 * quiz with instant per-question feedback and a running score.
 *
 * The authored lists are the single source of truth: questions/choices come
 * from the Review Exercises <ol> (choices in a nested <ol type="a">), the
 * correct answers from the letter in each Suggested Solutions item. The lists
 * are hidden only after a successful parse (no-JS keeps the plain lists).
 *
 * CONTENT RULE (Jess): the author's own choices/answers plus UI labels are the
 * baseline. Per-question feedback is OPT-IN and author-approved: if a Suggested
 * Solutions <li> carries a trailing `.quiz-why` element, its text is shown as a
 * "Why" panel after the student answers (grounded, publisher-verified copy —
 * same approval track as the Key Terms flip-card definitions). Chapters without
 * `.quiz-why` behave exactly as before (no explanations).
 * No persistence: this is in-the-moment self-assessment (like the flip cards).
 *
 * Evidence link: a `.quiz-why` may carry `data-quote` — a sentence copied
 * verbatim from this chapter's prose that the answer rests on. The panel then
 * ends with "Review 2.1 Price, Cost, and Value →", the section found by
 * locating the quote and walking back to its heading (never authored, so a
 * renumbered outline can't make it lie). Clicking it dispatches
 * `chapter-quiz:review`; the text-anchor controller highlights the sentence in
 * place and cancels the event. Uncancelled (no text-anchor on the page, or the
 * quote no longer matches) the link is a plain `#section` jump. A quote that
 * doesn't resolve when the panel opens gets no link at all.
 *
 * Getting back: a "Back to Q3 ↓" button is placed right after the highlighted
 * sentence (students rarely think of Back on a page that didn't navigate, and
 * on phones the back swipe feels like leaving). On the legacy reader the jump
 * also pushes a history entry (same URL, so no reload) and the entry it left
 * remembers the question, so the browser's Back button scrolls to that
 * question too; the button steps back through that entry so the two agree.
 * Under Turbo Drive (modern and OpenStax readers) history is off limits:
 * Turbo owns every entry, and popping onto one it marked starts a restore
 * visit, which with the reader's `turbo-cache-control: no-cache` is a network
 * fetch that rebuilds the body and wipes the quiz. There the button just
 * scrolls and Back is left to Turbo (it leaves the chapter, as on any page).
 *
 * Worked problems: a Review Exercise with no lettered choices (the grids and
 * computations of the sales comparison and income chapters) becomes an
 * unscored card with a "Show solution" button. Its Suggested Solutions <li>
 * has no `<strong>x)</strong>` lead; the whole item is the book's solution and
 * is shown as-is, under the why. A prompt that carries paragraphs or lists
 * (data set up for the question, a list of comparables) is rendered as the
 * book's own markup instead of being flattened to one line.
 *
 * The panel also carries any authored worked solution — a `.solution-work` block
 * or a table inside the Suggested Solutions <li>. Hiding the solutions list used
 * to swallow those outright, which cost the math-heavy modules (the Introduction
 * especially) their most useful feedback: the arithmetic. That markup is the
 * book's own, so it needs no approval track.
 *
 * Answers are final once given, so "start over" is the only way back. It is
 * offered in the header from the FIRST answer onward (Jimmy, 2026-08-17) —
 * previously the only reset lived in the completion banner, which stranded a
 * student who had answered two or three and wanted a clean run.
 *
 * Reading focus: `?questions=1-5,8-10` (or the `questions` value) limits the
 * quiz to those question numbers. Cards keep their printed numbers (Q8 stays
 * Q8), a note in the header says how many are assigned, and "Show all
 * questions" rebuilds the full quiz. A spec that matches nothing is ignored.
 *
 * OpenStax pages (the Section Quiz page of each chapter): questions are the
 * page's `[data-type="exercise"]` blocks (a prompt + an <ol type="a"> of
 * choices); the correct letter arrives as `data-answer`, stamped at render
 * time by Openstax::PagePresenter from the chapter's Answer Key page. Cards
 * are placed INLINE where each exercise was, so the page's own per-section
 * headings keep grouping them; one header (score, reset) goes above the
 * first section. OpenStax publishes answers for odd-numbered questions only:
 * a question with no key still takes an answer but is not graded — it gets a
 * "no answer key" panel that hands off to the Book Tutor when one is mounted.
 *
 * Usage: data-controller="chapter-quiz" on the theme wrapper element.
 */
export default class extends Controller {
  static values = {
    questions: { type: String, default: "" },
    // Remember picks in localStorage per page (OpenStax reader). The
    // "no answer key" flow hands off to the tutor, which is a round trip the
    // picks have to survive. Nothing reaches the server.
    persist: { type: Boolean, default: false }
  }

  connect() {
    this.onPopState = (event) => this.#popped(event)
    window.addEventListener("popstate", this.onPopState)

    const source = this.#authoredSource() || this.#openstaxSource()
    if (!source) return
    const { questions } = source

    questions.forEach((q, i) => (q.number = q.number ?? i + 1))
    this.inline = source.inline
    this.allQuestions = questions
    this.assignedQuestions = this.#assigned(questions)
    this.questions = this.assignedQuestions || questions
    this.answered = new Map()

    const quiz = this.#build()
    source.mount(quiz)
    if (this.persistValue) this.#restore()
  }

  disconnect() {
    window.removeEventListener("popstate", this.onPopState)
  }

  // Our themes: "Review Exercises" + "Suggested Solutions" <ol>s (appraisal).
  #authoredSource() {
    const heading = this.#findHeading("Review Exercises")
    const solutionsHeading = this.#findHeading("Suggested Solutions to Review Exercises")
    if (!heading || !solutionsHeading) return null

    const questionsOl = this.#nextOl(heading)
    const solutionsOl = this.#nextOl(solutionsHeading)
    if (!questionsOl || !solutionsOl) return null

    const questions = this.#parseQuestions(questionsOl)
    const answers = this.#parseAnswers(solutionsOl)
    if (!questions.length || questions.length !== answers.length) return null
    questions.forEach((q, i) => {
      q.worked = q.choices.length === 0
      q.correct = answers[i].index
      q.why = answers[i].why
      q.work = q.worked ? answers[i].solution : answers[i].work
      q.quote = answers[i].quote
    })
    // A lettered question needs a valid key; a worked problem must have none
    // (a stray letter means the two lists are out of step).
    const broken = (q) => (q.worked ? q.correct != null : q.correct == null || q.correct >= q.choices.length)
    if (questions.some(broken)) return null

    return {
      questions,
      inline: false,
      mount: (quiz) => {
        // chapter-outline may have dimmed the authored list (sections assigned but
        // no questions); the quiz stands in for that list, so it inherits the dim.
        if (questionsOl.classList.contains("focus-muted")) quiz.classList.add("focus-muted")
        questionsOl.insertAdjacentElement("beforebegin", quiz)

        // Hide the authored lists only once the quiz is live
        questionsOl.style.display = "none"
        solutionsHeading.style.display = "none"
        solutionsOl.style.display = "none"

        // The solutions section is gone from the reading flow, so drop its TOC entry
        // (chapter-outline connects first — same data-controller attribute order)
        if (solutionsHeading.id) {
          this.element
            .querySelector(`.chapter-outline a[href="#${solutionsHeading.id}"]`)
            ?.closest("li")
            ?.remove()
        }
      }
    }
  }

  // OpenStax: multiple-choice exercises with a stamped (or absent) answer.
  #openstaxSource() {
    const exercises = Array.from(this.element.querySelectorAll('[data-type="exercise"]')).filter((ex) =>
      ex.querySelector(".os-problem-container ol")
    )
    if (!exercises.length) return null

    const questions = exercises
      .map((ex) => {
        const clone = ex.querySelector(".os-problem-container").cloneNode(true)
        const nested = clone.querySelector("ol")
        const choices = nested ? Array.from(nested.children).map((c) => c.textContent.trim()) : []
        nested?.remove()
        const media = Array.from(clone.querySelectorAll("figure, table, .os-figure"))
        media.forEach((m) => m.remove())
        const number = parseInt(ex.querySelector(".os-number")?.textContent, 10)
        const letter = (ex.dataset.answer || "").trim().toLowerCase()
        const index = /^[a-z]$/.test(letter) ? letter.charCodeAt(0) - 97 : null
        // the page's own per-section heading links back to the section
        const sectionLink = ex.closest("section")?.querySelector("a[data-page-slug]")
        const review = sectionLink && {
          href: sectionLink.getAttribute("href"),
          label: sectionLink.querySelector(".os-number")?.textContent.trim() || sectionLink.textContent.trim()
        }
        return {
          id: ex.id,
          prompt: clone.textContent.trim().replace(/\s+/g, " "),
          choices,
          media,
          correct: index != null && index < choices.length ? index : null,
          // stamped by Openstax::PagePresenter from db/openstax_answers/<book>.yml
          answerSource: ex.dataset.answerSource || null,
          why: ex.dataset.answerWhy || "",
          ref: ex.dataset.answerRef || null,
          work: [],
          number: Number.isFinite(number) ? number : undefined,
          anchor: ex,
          review
        }
      })
      .filter((q) => q.choices.length >= 2)
    if (!questions.length) return null

    return {
      questions,
      inline: true,
      mount: (quiz) => {
        const first = questions[0].anchor
        const section = first.closest("section") || first
        section.insertAdjacentElement("beforebegin", quiz)
        questions.forEach((q) => (q.anchor.style.display = "none"))
      }
    }
  }

  // The subset selected by the `questions` spec, or null for "all".
  #assigned(questions) {
    const spec = parseSpec(this.questionsValue || paramFromUrl(QUESTIONS_PARAM))
    if (spec.empty || spec.all) return null
    const picked = questions.filter((q) => specMatchesNumber(spec, q.number))
    return picked.length && picked.length < questions.length ? picked : null
  }

  #findHeading(text) {
    return Array.from(this.element.querySelectorAll("h2")).find(
      (h) => h.textContent.trim().replace(/^[\d.\s]+/, "") === text
    )
  }

  #nextOl(el) {
    let node = el.nextElementSibling
    while (node && node.tagName !== "OL") node = node.nextElementSibling
    return node
  }

  #parseQuestions(ol) {
    return Array.from(ol.children).map((li) => {
      const clone = li.cloneNode(true)
      const nested = clone.querySelector("ol")
      const choices = nested
        ? Array.from(nested.children).map((c) => c.textContent.trim())
        : []
      nested?.remove()
      const prompt = clone.textContent.trim().replace(/\s+/g, " ")
      // Paragraphs or lists in the prompt (data set up for the question, a
      // list of comparables): keep the book's markup, tables and all.
      if (Array.from(clone.children).some((el) => el.matches("p, ul, ol"))) {
        return { prompt, promptNodes: Array.from(clone.childNodes), choices, media: [] }
      }
      // Pull out figures/tables so they render as authored artwork instead of
      // being flattened into the prompt text (e.g. the Basic Math geometry SVGs
      // and the cost-to-build table). Detaching them also keeps their labels and
      // captions out of the prompt string.
      const media = Array.from(clone.querySelectorAll("figure, table"))
      media.forEach((m) => m.remove())
      return { prompt, choices, media }
    })
  }

  #parseAnswers(ol) {
    return Array.from(ol.children).map((li) => {
      // Clone so pulling the optional `.quiz-why` out doesn't mutate the
      // (hidden) authored list that the no-JS fallback relies on.
      const clone = li.cloneNode(true)
      const why = clone.querySelector(".quiz-why")
      const whyText = why ? why.textContent.trim().replace(/\s+/g, " ") : ""
      const quote = why?.dataset.quote?.trim() || ""
      why?.remove()
      // Detached originals; the panel re-clones them so a reset can rebuild it.
      // Keep only outermost matches — a table inside a .solution-work would
      // otherwise be captured twice and orphaned when its parent is removed.
      const work = Array.from(clone.querySelectorAll(".solution-work, table")).filter(
        (node, _i, all) => !all.some((other) => other !== node && other.contains(node))
      )
      work.forEach((node) => node.remove())
      // The key is a "<strong>b)</strong>" lead; a bold total in a worked
      // solution's table is not a key.
      const letter = clone.querySelector("strong")?.textContent.trim().match(/^([a-z])\)/i)?.[1].toLowerCase()
      const index = letter ? letter.charCodeAt(0) - 97 : null
      // A worked problem's whole item (minus the why) is the book's solution
      const whole = li.cloneNode(true)
      whole.querySelectorAll(".quiz-why").forEach((node) => node.remove())
      const solution = Array.from(whole.childNodes).filter((n) => n.nodeType === 1 || n.textContent.trim())
      return { index, why: whyText, work, quote, solution }
    })
  }

  #build() {
    this.quizEl = document.createElement("div")
    this.quizEl.className = "chapter-quiz"

    const header = document.createElement("div")
    header.className = "quiz-header"
    const subline = document.createElement("p")
    subline.className = "quiz-subline"
    const keyed = this.#keyed()
    const worked = this.questions.filter((q) => q.worked).length
    subline.textContent = keyed === 0 && worked === this.questions.length
      ? "Work each problem on paper, then show the solution."
      : keyed === this.questions.length
      ? "Pick an answer to check it instantly."
      : worked === this.questions.length - keyed
        ? `Pick an answer to check it instantly. Work the ${worked === 1 ? "worked problem" : `${worked} worked problems`} on paper, then show the solution.`
        : `Pick an answer to check it instantly. ${keyed} of ${this.questions.length} questions can be checked.`
    header.appendChild(subline)

    const actions = document.createElement("div")
    actions.className = "quiz-header-actions"
    actions.appendChild(this.#scoreChip())
    actions.appendChild(this.#resetButton())
    header.appendChild(actions)
    this.quizEl.appendChild(header)
    if (this.assignedQuestions) this.quizEl.appendChild(this.#filterNote())

    this.#buildCards()
    return this.quizEl
  }

  #buildCards() {
    this.cards = this.questions.map((q, i) => this.#questionCard(q, i))
    this.cards.forEach((card, i) => {
      const anchor = this.inline && this.questions[i].anchor
      if (anchor) anchor.insertAdjacentElement("beforebegin", card)
      else this.quizEl.appendChild(card)
    })
  }

  // Questions that can be graded (OpenStax publishes odd-numbered answers only)
  #keyed() {
    return this.questions.filter((q) => q.correct != null).length
  }

  // "Showing 5 of 23 assigned questions · Show all questions"
  #filterNote() {
    this.filterNote = document.createElement("div")
    this.filterNote.className = "quiz-filter"
    this.filterText = document.createElement("span")
    this.filterNote.appendChild(this.filterText)
    this.filterBtn = document.createElement("button")
    this.filterBtn.type = "button"
    this.filterBtn.className = "quiz-filter-toggle"
    this.filterBtn.addEventListener("click", () => this.#toggleAssigned())
    this.filterNote.appendChild(this.filterBtn)
    this.#updateFilterNote()
    return this.filterNote
  }

  #updateFilterNote() {
    const assigned = this.assignedQuestions.length
    const total = this.allQuestions.length
    const showingAll = this.questions === this.allQuestions
    this.filterText.textContent = showingAll
      ? `Showing all ${total} questions (${assigned} assigned)`
      : `Showing the ${assigned} assigned questions of ${total}`
    this.filterBtn.textContent = showingAll ? "Back to assigned questions" : "Show all questions"
  }

  // Swapping the question set restarts the quiz (answers are per question set).
  #toggleAssigned() {
    this.questions = this.questions === this.allQuestions ? this.assignedQuestions : this.allQuestions
    this.answered.clear()
    this.banner?.remove()
    this.banner = null
    this.cards.forEach((card) => card.remove())
    this.#buildCards()
    this.#updateFilterNote()
    this.#updateScore()
  }

  #scoreChip() {
    this.chip = document.createElement("div")
    this.chip.className = "quiz-score-chip"
    const [scoreLabel, answeredLabel, answered] = this.inline
      ? ["Correct", "Answered", `0 of ${this.questions.length}`]
      : ["Score", "Answered", `0/${this.questions.length}`]
    const answeredStat = `<div class="quiz-stat"><span class="quiz-stat-num" data-stat="answered">${answered}</span><span class="quiz-stat-label">${answeredLabel}</span></div>`
    // Worked problems only (ch 24): there is no score, so don't show "0/0"
    this.chip.innerHTML = this.#keyed() === 0 && !this.inline
      ? answeredStat.replace(answeredLabel, "Solved")
      : `<div class="quiz-stat"><span class="quiz-stat-num" data-stat="score">0</span><span class="quiz-stat-label">${scoreLabel}</span></div>` +
        '<div class="quiz-stat-divider"></div>' + answeredStat
    return this.chip
  }

  // Hidden until the first answer — there is nothing to start over from before
  // that, and an always-visible reset reads as a required step.
  #resetButton() {
    this.resetBtn = document.createElement("button")
    this.resetBtn.type = "button"
    this.resetBtn.className = "quiz-reset"
    this.resetBtn.textContent = "Start over"
    this.resetBtn.hidden = true
    this.resetBtn.addEventListener("click", () => this.#reset())
    return this.resetBtn
  }

  #questionCard(question, index) {
    const card = document.createElement("div")
    card.className = "quiz-question"
    card.dataset.quizNumber = String(question.number ?? index + 1)
    if (question.worked) card.classList.add("is-worked")
    else if (question.correct == null) card.classList.add("no-key")

    const promptRow = document.createElement("div")
    promptRow.className = "quiz-prompt"
    const badge = document.createElement("span")
    badge.className = "quiz-qnum"
    badge.textContent = `Q${question.number ?? index + 1}`
    const prompt = document.createElement("span")
    prompt.className = "quiz-prompt-text"
    prompt.textContent = question.prompt
    promptRow.appendChild(badge)
    // Only keyed questions get a tag. An unkeyed one is just a question —
    // "Tutor question" meant nothing to a student with no tutor (Jess,
    // 2026-09-09); what happens after answering says what it needs to.
    // The tag names the answer's provenance. "AI check" is not decoration:
    // the student is entitled to know that OpenStax withheld this answer and
    // we generated it (docs/openstax-answers.md).
    if (this.inline && question.correct != null) {
      const ai = question.answerSource === "ai"
      const tag = document.createElement("span")
      tag.className = ai ? "quiz-tag is-ai" : "quiz-tag"
      tag.textContent = ai ? "AI check" : "Instant check"
      if (ai) tag.title = "OpenStax doesn't publish an answer for this question. This one was written from the section and reviewed."
      promptRow.appendChild(tag)
    }
    if (question.worked) {
      const tag = document.createElement("span")
      tag.className = "quiz-tag is-worked"
      tag.textContent = "Worked problem"
      promptRow.appendChild(tag)
    }
    // A rich prompt goes below the number row as the book's own markup; the
    // one-line prompt sits in the row beside the number, as before.
    if (question.promptNodes) {
      card.appendChild(promptRow)
      const body = document.createElement("div")
      body.className = "quiz-prompt-body"
      question.promptNodes.forEach((node) => body.appendChild(node.cloneNode(true)))
      card.appendChild(body)
    } else {
      promptRow.appendChild(prompt)
      card.appendChild(promptRow)
    }

    if (question.media && question.media.length) {
      const figure = document.createElement("div")
      figure.className = "quiz-figure"
      question.media.forEach((node) => figure.appendChild(node))
      card.appendChild(figure)
    }

    const choices = document.createElement("div")
    choices.className = "quiz-choices"
    question.choices.forEach((text, choiceIndex) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "quiz-choice"
      const letter = document.createElement("span")
      letter.className = "quiz-choice-badge"
      letter.textContent = String.fromCharCode(65 + choiceIndex)
      const label = document.createElement("span")
      label.className = "quiz-choice-text"
      label.textContent = text
      btn.appendChild(letter)
      btn.appendChild(label)
      btn.addEventListener("click", () => this.#answer(index, choiceIndex))
      choices.appendChild(btn)
    })
    if (question.worked) {
      const show = document.createElement("button")
      show.type = "button"
      show.className = "quiz-show-solution"
      show.textContent = "Show solution"
      show.addEventListener("click", () => this.#revealSolution(index))
      choices.appendChild(show)
    }
    card.appendChild(choices)
    return card
  }

  // A worked problem: nothing to grade, so "answering" it means asking for the
  // book's solution once the student has tried it on paper. It counts toward
  // "answered" (so the quiz can complete) but never toward the score.
  #revealSolution(questionIndex) {
    if (this.answered.has(questionIndex)) return
    this.answered.set(questionIndex, null)

    const question = this.questions[questionIndex]
    const card = this.cards[questionIndex]
    card.classList.add("revealed")
    const show = card.querySelector(".quiz-show-solution")
    if (show) show.hidden = true

    const panel = this.#explanation(question)
    if (panel) {
      card.appendChild(panel)
      // The button that had focus is gone; hand focus to what it revealed
      panel.setAttribute("tabindex", "-1")
      panel.focus({ preventScroll: true })
    }

    this.#updateScore()
    if (this.answered.size === this.questions.length) this.#complete()
  }

  #answer(questionIndex, choiceIndex) {
    if (this.answered.has(questionIndex)) return
    this.answered.set(questionIndex, choiceIndex)
    this.#persist()

    const question = this.questions[questionIndex]
    const card = this.cards[questionIndex]
    card.classList.add("revealed")
    const buttons = card.querySelectorAll(".quiz-choice")

    if (question.correct == null) {
      buttons.forEach((btn, i) => {
        btn.setAttribute("aria-disabled", "true")
        btn.classList.add(i === choiceIndex ? "selected" : "dimmed")
      })
      card.appendChild(this.#noKeyPanel(question, choiceIndex))
      this.#updateScore()
      if (this.answered.size === this.questions.length) this.#complete()
      return
    }

    buttons.forEach((btn, i) => {
      btn.setAttribute("aria-disabled", "true")
      const badge = btn.querySelector(".quiz-choice-badge")
      if (i === question.correct) {
        btn.classList.add("correct")
        if (i !== choiceIndex) btn.classList.add("revealed-key")
        badge.textContent = "✓"
        btn.appendChild(this.#srNote(i === choiceIndex ? "Correct" : "Correct answer"))
      } else if (i === choiceIndex) {
        btn.classList.add("incorrect")
        badge.textContent = "✗"
        btn.appendChild(this.#srNote("Your answer, incorrect"))
      } else {
        btn.classList.add("dimmed")
      }
    })

    const panel = this.inline
      ? this.#verdict(choiceIndex === question.correct, question.correct, question)
      : this.#explanation(question)
    if (panel) card.appendChild(panel)

    this.#updateScore()
    if (this.answered.size === this.questions.length) this.#complete()
  }

  #explanation(question) {
    const { why: text, work } = question
    if (!text && !work?.length) return null

    const panel = document.createElement("div")
    panel.className = "quiz-explanation"
    panel.setAttribute("role", "note")
    const label = document.createElement("span")
    label.className = "quiz-explanation-label"
    label.textContent = question.worked ? "Solution" : "Why"
    panel.appendChild(label)

    if (text) {
      const body = document.createElement("p")
      body.className = "quiz-explanation-text"
      body.textContent = text
      panel.appendChild(body)
    }

    if (work?.length) {
      const shown = document.createElement("div")
      shown.className = "quiz-explanation-work"
      work.forEach((node) => shown.appendChild(node.cloneNode(true)))
      panel.appendChild(shown)
    }

    const review = this.#evidenceLink(question)
    if (review) panel.appendChild(review)

    return panel
  }

  // "Review 2.1 Price, Cost, and Value →" for an authored `data-quote`, or
  // null when there is no quote or it no longer matches the chapter.
  #evidenceLink(question) {
    const { quote, number } = question
    if (!quote) return null

    const scope = this.element.querySelector(".module-content-wrapper") || this.element
    const node = locateQuote(scope, quote)
    if (!node) return null

    const heading = sectionHeading(scope, node)
    const label = sectionLabel(heading)

    const actions = document.createElement("div")
    actions.className = "quiz-explanation-actions"
    const link = document.createElement("a")
    link.className = "quiz-review-link"
    link.href = heading?.id ? `#${heading.id}` : "#"
    link.textContent = label ? `Review ${label} \u2192` : "Review the passage \u2192"
    link.addEventListener("click", (event) => {
      this.#removeReturnButtons()
      const review = this.dispatch("review", { detail: { quote } })
      if (review.defaultPrevented) {
        event.preventDefault()
        this.#rememberReturn(number)
        if (review.detail.marks?.length) this.#returnButton(number, review.detail.marks)
      } else if (!heading?.id) {
        event.preventDefault()
      }
    })
    actions.appendChild(link)
    return actions
  }

  // The entry we're leaving learns which question to come back to; the jump
  // gets an entry of its own. Same URL on both, so Back never reloads.
  // Not under Turbo Drive: Turbo stamps `state.turbo` on every entry it owns
  // and treats a pop onto one as a restore visit (see "Getting back" above),
  // so there we leave history alone and the button falls through to #returnTo.
  #rememberReturn(number) {
    if (this.#turboDrivesHistory()) return
    history.replaceState({ ...(history.state || {}), chapterQuizReturn: number }, "")
    history.pushState({ chapterQuizReview: number }, "")
  }

  #turboDrivesHistory() {
    return history.state?.turbo != null
  }

  #popped(event) {
    const state = event.state || {}
    if (state.chapterQuizReturn != null) {
      this.#returnTo(state.chapterQuizReturn)
    } else if (state.chapterQuizReview != null) {
      // Forward again: the highlight is still painted, so go back to it
      this.element
        .querySelector("mark.text-anchor-mark")
        ?.scrollIntoView({ behavior: scrollBehavior(), block: "center" })
    }
  }

  #returnTo(number) {
    this.#removeReturnButtons()
    const card = this.cards?.find((c) => c.dataset.quizNumber === String(number))
    if (!card) return
    card.scrollIntoView({ behavior: scrollBehavior(), block: "center" })
    card.querySelector(".quiz-review-link")?.focus({ preventScroll: true })
  }

  // "Back to Q3 ↓", right after the highlighted sentence. It takes focus so a
  // keyboard user who followed the link is one keypress from the question.
  #returnButton(number, marks) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "quiz-return"
    button.setAttribute("aria-label", `Back to question ${number}`)
    button.textContent = `Back to Q${number}`
    const arrow = document.createElement("span")
    arrow.setAttribute("aria-hidden", "true")
    arrow.textContent = " \u2193"
    button.appendChild(arrow)
    button.addEventListener("click", () => {
      // Our own entry is current (legacy reader): step back through history so
      // Back and this button agree (and the history doesn't grow). Otherwise
      // (Turbo, or the entry is gone) just scroll.
      if (history.state?.chapterQuizReview === number) history.back()
      else this.#returnTo(number)
    })
    marks[marks.length - 1].insertAdjacentElement("afterend", button)
    button.focus({ preventScroll: true })
  }

  #removeReturnButtons() {
    this.element.querySelectorAll(".quiz-return").forEach((button) => {
      const parent = button.parentNode
      button.remove()
      parent?.normalize()
    })
  }

  // OpenStax keeps even-numbered answers in the instructor guide. Say so, and
  // open the Book Tutor when the page has one (its launcher is outside our
  // wrapper, so we drive it by clicking it).
  #noKeyPanel(question, choiceIndex) {
    const panel = document.createElement("div")
    panel.className = "quiz-explanation quiz-nokey"
    panel.setAttribute("role", "note")
    const label = document.createElement("span")
    label.className = "quiz-explanation-label"
    label.textContent = "No published answer"
    panel.appendChild(label)
    const body = document.createElement("p")
    body.className = "quiz-explanation-text"
    // The word "tutor" only appears when a Book Tutor is actually mounted on
    // the course; otherwise the student has nobody to ask by that name.
    const launcher = document.querySelector(".book-tutor__launcher")
    const letter = String.fromCharCode(97 + choiceIndex)
    const saved = this.persistValue ? ` We saved your pick (${letter} \u00B7 ${question.choices[choiceIndex]}).` : ""
    body.textContent = launcher
      ? `OpenStax doesn't publish an answer for this question, so it isn't scored.${saved} Ask AiMee to work through it with you.`
      : `OpenStax doesn't publish an answer for this question, so it isn't scored.${saved}`
    panel.appendChild(body)

    const actions = document.createElement("div")
    actions.className = "quiz-nokey-actions"
    if (launcher) {
      const ask = document.createElement("button")
      ask.type = "button"
      ask.className = "quiz-nokey-btn"
      ask.textContent = "Ask AiMee"
      ask.addEventListener("click", () => {
        this.dispatch("ask", { detail: { prompt: question.prompt, choices: question.choices, pick: choiceIndex } })
        // open, never toggle: the launcher's own action closes an open drawer
        const host = launcher.closest('[data-controller~="book-tutor"]')
        const tutor = host && this.application.getControllerForElementAndIdentifier(host, "book-tutor")
        if (tutor) tutor.open()
        else launcher.click()
      })
      actions.appendChild(ask)
    }
    if (question.review?.href) {
      const review = document.createElement("a")
      review.className = "quiz-nokey-review"
      review.href = question.review.href
      review.textContent = `Review ${question.review.label} \u2192`
      actions.appendChild(review)
    }
    if (actions.childElementCount) panel.appendChild(actions)
    return panel
  }

  // Answered OpenStax question: the verdict, why that answer is right, and a
  // link back to the sentence it rests on. Before the answer set existed this
  // was the verdict line alone; the "why" is the part students actually asked
  // for, and the citation is what keeps it checkable.
  #verdict(correct, correctIndex, question) {
    const panel = document.createElement("div")
    panel.className = `quiz-explanation${correct ? "" : " is-incorrect"}`
    panel.setAttribute("role", "status")

    const letter = String.fromCharCode(65 + correctIndex)
    const named = question?.answerSource === "ai" ? `the answer is ${letter}` : `the answer key says ${letter}`

    const label = document.createElement("span")
    label.className = "quiz-explanation-label"
    label.textContent = correct ? "Correct" : "Not quite"
    panel.appendChild(label)

    const body = document.createElement("p")
    body.className = "quiz-explanation-text"
    // With an explanation the verdict sentence would just be in the way: the
    // label already says correct or not, so the body carries the reasoning.
    body.textContent = question?.why
      ? (correct ? question.why : `Not quite \u2014 ${named}. ${question.why}`)
      : (correct ? "Correct." : `Not quite \u2014 ${named}.`)
    panel.appendChild(body)

    if (question?.ref) {
      const actions = document.createElement("div")
      actions.className = "quiz-explanation-actions"
      const link = document.createElement("a")
      link.className = "quiz-review-link"
      link.href = question.ref
      link.textContent = `Review ${question.review?.label || "the section"} \u2192`
      actions.appendChild(link)
      panel.appendChild(actions)
    }
    return panel
  }

  // ---- persistence (persist value; OpenStax reader) ----------------------

  get #storageKey() {
    return `chapter-quiz:${window.location.pathname}`
  }

  #persist() {
    if (!this.persistValue) return
    try {
      const picks = {}
      this.answered.forEach((choice, i) => {
        const id = this.questions[i].id
        if (id) picks[id] = choice
      })
      localStorage.setItem(this.#storageKey, JSON.stringify(picks))
    } catch (_) {
      // storage unavailable (private mode, quota): the quiz just doesn't remember
    }
  }

  #restore() {
    let picks
    try {
      picks = JSON.parse(localStorage.getItem(this.#storageKey) || "{}")
    } catch (_) {
      return
    }
    this.questions.forEach((q, i) => {
      const choice = picks[q.id]
      if (Number.isInteger(choice) && choice < q.choices.length) this.#answer(i, choice)
    })
  }

  #forget() {
    if (!this.persistValue) return
    try {
      localStorage.removeItem(this.#storageKey)
    } catch (_) {
      // ignore
    }
  }

  #srNote(text) {
    const note = document.createElement("span")
    note.className = "quiz-sr-only"
    note.textContent = ` (${text})`
    return note
  }

  #score() {
    let score = 0
    this.answered.forEach((choice, i) => {
      const correct = this.questions[i].correct
      if (correct != null && choice === correct) score++
    })
    return score
  }

  #updateScore() {
    const keyed = this.#keyed()
    const scoreStat = this.chip.querySelector('[data-stat="score"]')
    if (scoreStat) {
      scoreStat.textContent = keyed === this.questions.length && !this.inline ? this.#score() : `${this.#score()}/${keyed}`
    }
    this.chip.querySelector('[data-stat="answered"]').textContent = this.inline
      ? `${this.answered.size} of ${this.questions.length}`
      : `${this.answered.size}/${this.questions.length}`
    this.resetBtn.hidden = this.answered.size === 0
    // mirror the score into the reading-progress status bar
    this.dispatch("score", {
      detail: { score: this.#score(), answered: this.answered.size, total: this.questions.length, keyed }
    })
  }

  #complete() {
    this.banner = document.createElement("div")
    this.banner.className = "quiz-complete"
    const result = document.createElement("div")
    result.className = "quiz-complete-score"
    const keyed = this.#keyed()
    result.textContent = keyed === 0
      ? `You've worked all ${this.questions.length} problem${this.questions.length === 1 ? "" : "s"}.`
      : `You scored ${this.#score()} / ${keyed}`
    if (keyed > 0 && keyed < this.questions.length) {
      const note = document.createElement("p")
      note.className = "quiz-complete-note"
      const unscored = this.questions.length - keyed
      const worked = this.questions.filter((q) => q.worked).length
      note.textContent = worked === unscored
        ? `${worked} worked problem${worked === 1 ? " isn't" : "s aren't"} scored. Check your work against the book's solution.`
        : `${unscored} question${unscored === 1 ? "" : "s"} without an answer weren't scored.`
      this.banner.appendChild(note)
    }
    const retake = document.createElement("button")
    retake.type = "button"
    retake.className = "quiz-retake"
    retake.textContent = "Retake quiz"
    retake.addEventListener("click", () => this.#reset())
    this.banner.prepend(result)
    this.banner.appendChild(retake)
    if (this.inline) this.cards[this.cards.length - 1].insertAdjacentElement("afterend", this.banner)
    else this.quizEl.appendChild(this.banner)
  }

  #reset() {
    this.#removeReturnButtons()
    this.answered.clear()
    this.#forget()
    this.banner?.remove()
    this.banner = null
    this.cards.forEach((card) => {
      card.classList.remove("revealed")
      card.querySelector(".quiz-explanation")?.remove()
      card.querySelectorAll(".quiz-choice").forEach((btn, i) => {
        btn.classList.remove("correct", "incorrect", "dimmed", "selected", "revealed-key")
        btn.removeAttribute("aria-disabled")
        btn.querySelector(".quiz-sr-only")?.remove()
        btn.querySelector(".quiz-choice-badge").textContent = String.fromCharCode(65 + i)
      })
      const show = card.querySelector(".quiz-show-solution")
      if (show) show.hidden = false
    })
    this.#updateScore()
    this.quizEl.scrollIntoView({ behavior: "smooth", block: "start" })
    // Both reset controls remove/hide themselves, so keyboard focus would be
    // dropped to <body>. Hand it to the first choice of the fresh quiz.
    this.cards[0]?.querySelector(".quiz-choice")?.focus()
  }
}
